import React, { useState, useEffect, useRef } from 'react';
import { superadminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Shield,
    Mountain,
    Building2,
    Factory,
    Trees,
    Palette,
    Upload,
    Link,
    Save,
    Loader2,
    Check,
    Eye,
    Type,
    Image as ImageIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
    {
        id: 'minero',
        name: 'Minero',
        icon: Mountain,
        description: 'Mina de cobre, tonos ocean blue',
        background_url: 'https://images.unsplash.com/photo-1582161095006-7c4edf743cd9?w=1920&q=80',
        accent_color: '#22D3EE',
        overlay_from: '#0A1929',
        overlay_via: '#0A1929',
    },
    {
        id: 'corporativo',
        name: 'Corporativo',
        icon: Building2,
        description: 'Skyline urbano, tonos azul oscuro',
        background_url: 'https://images.pexels.com/photos/1787044/pexels-photo-1787044.jpeg?w=1920&q=80',
        accent_color: '#60A5FA',
        overlay_from: '#0F172A',
        overlay_via: '#0F172A',
    },
    {
        id: 'industrial',
        name: 'Industrial',
        icon: Factory,
        description: 'Maquinaria pesada, tonos ambar',
        background_url: 'https://images.pexels.com/photos/6726394/pexels-photo-6726394.jpeg?w=1920&q=80',
        accent_color: '#F59E0B',
        overlay_from: '#1C1917',
        overlay_via: '#1C1917',
    },
    {
        id: 'naturaleza',
        name: 'Naturaleza',
        icon: Trees,
        description: 'Montañas y paisaje, tonos verde',
        background_url: 'https://images.pexels.com/photos/15103188/pexels-photo-15103188.png?w=1920&q=80',
        accent_color: '#34D399',
        overlay_from: '#064E3B',
        overlay_via: '#064E3B',
    },
];

const DEFAULT_CONFIG = {
    preset: 'minero',
    background_url: PRESETS[0].background_url,
    icon_name: 'Shield',
    title: 'PROTECCION INDUSTRIAL',
    subtitle: 'Minera Los Pelambres',
    description: 'Gestion de guias de despacho, solicitudes de permisos y control operacional.',
    accent_color: '#22D3EE',
    overlay_from: '#0A1929',
    overlay_via: '#0A1929',
    card_title: 'Acceso al Sistema',
    card_description: 'Ingrese sus credenciales para continuar',
    footer_text: 'Minera Los Pelambres - Proteccion Industrial',
};

export default function LoginConfigPanel() {
    const [config, setConfig] = useState({ ...DEFAULT_CONFIG });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [customBgUrl, setCustomBgUrl] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        setLoading(true);
        try {
            const res = await superadminAPI.getLoginConfig();
            setConfig({ ...DEFAULT_CONFIG, ...res.data });
        } catch (err) {
            console.error('Error loading login config:', err);
        } finally {
            setLoading(false);
        }
    };

    const applyPreset = (presetId) => {
        const preset = PRESETS.find(p => p.id === presetId);
        if (!preset) return;
        setConfig(prev => ({
            ...prev,
            preset: preset.id,
            background_url: preset.background_url,
            accent_color: preset.accent_color,
            overlay_from: preset.overlay_from,
            overlay_via: preset.overlay_via,
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = { ...config };
            delete payload.id;
            delete payload.updated_at;
            await superadminAPI.updateLoginConfig(payload);
            toast.success('Configuracion de login guardada');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleUploadBg = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const res = await superadminAPI.uploadLoginBg(file);
            const url = res.data.url;
            setConfig(prev => ({ ...prev, background_url: url, preset: 'custom' }));
            toast.success('Imagen de fondo subida');
        } catch (err) {
            toast.error('Error al subir imagen');
        } finally {
            setUploading(false);
        }
    };

    const handleApplyUrl = () => {
        if (!customBgUrl.trim()) return;
        setConfig(prev => ({ ...prev, background_url: customBgUrl.trim(), preset: 'custom' }));
        setCustomBgUrl('');
        toast.success('URL de fondo aplicada');
    };

    const resolveUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        return `${BACKEND_URL}${url}`;
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* ── Section 1: Preset Themes ── */}
            <Card className="border-2">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Palette className="h-5 w-5" />
                        Temas Predefinidos
                    </CardTitle>
                    <CardDescription>Selecciona un tema base para la pagina de login</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {PRESETS.map(preset => {
                            const Icon = preset.icon;
                            const isActive = config.preset === preset.id;
                            return (
                                <button
                                    key={preset.id}
                                    onClick={() => applyPreset(preset.id)}
                                    className={`relative rounded-lg border-2 overflow-hidden text-left transition-all group ${
                                        isActive
                                            ? 'border-primary ring-2 ring-primary/30'
                                            : 'border-border hover:border-muted-foreground'
                                    }`}
                                    data-testid={`preset-${preset.id}`}
                                >
                                    <div
                                        className="h-24 bg-cover bg-center"
                                        style={{ backgroundImage: `url(${preset.background_url})` }}
                                    >
                                        <div className="h-full w-full bg-black/40 flex items-center justify-center">
                                            <Icon className="h-8 w-8 text-white/80" />
                                        </div>
                                    </div>
                                    <div className="p-2">
                                        <p className="font-medium text-sm flex items-center gap-1">
                                            {isActive && <Check className="h-3.5 w-3.5 text-primary" />}
                                            {preset.name}
                                        </p>
                                        <p className="text-xs text-muted-foreground">{preset.description}</p>
                                    </div>
                                    <div
                                        className="absolute top-2 right-2 w-4 h-4 rounded-full border-2 border-white/50"
                                        style={{ backgroundColor: preset.accent_color }}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* ── Section 2: Background Image ── */}
            <Card className="border-2">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <ImageIcon className="h-5 w-5" />
                        Imagen de Fondo
                    </CardTitle>
                    <CardDescription>Sube una imagen o pega una URL desde cualquier pagina web</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Current preview */}
                    <div
                        className="h-40 rounded-lg bg-cover bg-center border-2 border-dashed"
                        style={{ backgroundImage: `url(${resolveUrl(config.background_url)})` }}
                    >
                        <div
                            className="h-full w-full rounded-lg flex items-end p-3"
                            style={{
                                background: `linear-gradient(to top, ${config.overlay_from}E6, ${config.overlay_via}66, transparent)`,
                            }}
                        >
                            <p className="text-white/70 text-xs truncate max-w-full">{config.background_url}</p>
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="border-2"
                            data-testid="upload-bg-btn"
                        >
                            {uploading ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <Upload className="h-4 w-4 mr-2" />
                            )}
                            Subir Imagen
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleUploadBg}
                        />
                        <div className="flex-1 flex gap-2">
                            <Input
                                placeholder="Pegar URL de imagen (ej: https://unsplash.com/...)"
                                value={customBgUrl}
                                onChange={(e) => setCustomBgUrl(e.target.value)}
                                className="flex-1"
                                data-testid="bg-url-input"
                            />
                            <Button
                                variant="outline"
                                onClick={handleApplyUrl}
                                disabled={!customBgUrl.trim()}
                                className="border-2"
                                data-testid="apply-bg-url-btn"
                            >
                                <Link className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>

                    {/* Overlay colors */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Color Overlay (base)</Label>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="color"
                                    value={config.overlay_from}
                                    onChange={(e) => setConfig(p => ({ ...p, overlay_from: e.target.value }))}
                                    className="w-10 h-8 rounded cursor-pointer border"
                                />
                                <Input
                                    value={config.overlay_from}
                                    onChange={(e) => setConfig(p => ({ ...p, overlay_from: e.target.value }))}
                                    className="font-mono text-xs h-8"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Color Overlay (medio)</Label>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="color"
                                    value={config.overlay_via}
                                    onChange={(e) => setConfig(p => ({ ...p, overlay_via: e.target.value }))}
                                    className="w-10 h-8 rounded cursor-pointer border"
                                />
                                <Input
                                    value={config.overlay_via}
                                    onChange={(e) => setConfig(p => ({ ...p, overlay_via: e.target.value }))}
                                    className="font-mono text-xs h-8"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Color Acento</Label>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="color"
                                    value={config.accent_color}
                                    onChange={(e) => setConfig(p => ({ ...p, accent_color: e.target.value }))}
                                    className="w-10 h-8 rounded cursor-pointer border"
                                />
                                <Input
                                    value={config.accent_color}
                                    onChange={(e) => setConfig(p => ({ ...p, accent_color: e.target.value }))}
                                    className="font-mono text-xs h-8"
                                />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── Section 3: Texts ── */}
            <Card className="border-2">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Type className="h-5 w-5" />
                        Textos
                    </CardTitle>
                    <CardDescription>Personaliza los textos que aparecen en la pagina de login</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold">Titulo Principal</Label>
                            <Input
                                value={config.title}
                                onChange={(e) => setConfig(p => ({ ...p, title: e.target.value }))}
                                placeholder="PROTECCION INDUSTRIAL"
                                data-testid="config-title"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold">Subtitulo</Label>
                            <Input
                                value={config.subtitle}
                                onChange={(e) => setConfig(p => ({ ...p, subtitle: e.target.value }))}
                                placeholder="Minera Los Pelambres"
                                data-testid="config-subtitle"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold">Descripcion</Label>
                        <Input
                            value={config.description}
                            onChange={(e) => setConfig(p => ({ ...p, description: e.target.value }))}
                            placeholder="Gestion de guias de despacho..."
                            data-testid="config-description"
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold">Titulo del Card</Label>
                            <Input
                                value={config.card_title}
                                onChange={(e) => setConfig(p => ({ ...p, card_title: e.target.value }))}
                                placeholder="Acceso al Sistema"
                                data-testid="config-card-title"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs font-semibold">Descripcion del Card</Label>
                            <Input
                                value={config.card_description}
                                onChange={(e) => setConfig(p => ({ ...p, card_description: e.target.value }))}
                                placeholder="Ingrese sus credenciales..."
                                data-testid="config-card-desc"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-xs font-semibold">Texto del Pie</Label>
                        <Input
                            value={config.footer_text}
                            onChange={(e) => setConfig(p => ({ ...p, footer_text: e.target.value }))}
                            placeholder="Minera Los Pelambres - Proteccion Industrial"
                            data-testid="config-footer"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* ── Section 4: Preview ── */}
            <Card className="border-2">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Eye className="h-5 w-5" />
                        Vista Previa
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="rounded-lg overflow-hidden border-2 shadow-lg" style={{ height: '320px' }}>
                        <div className="h-full grid grid-cols-2">
                            {/* Left - hero */}
                            <div
                                className="bg-cover bg-center relative"
                                style={{ backgroundImage: `url(${resolveUrl(config.background_url)})` }}
                            >
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: `linear-gradient(to top, ${config.overlay_from}E6, ${config.overlay_via}66, transparent)`,
                                    }}
                                />
                                <div className="relative z-10 h-full flex flex-col justify-end p-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Shield className="h-5 w-5" style={{ color: config.accent_color }} />
                                        <span className="text-white font-black text-sm uppercase tracking-tight">
                                            {config.title}
                                        </span>
                                    </div>
                                    <p className="text-white/90 text-xs font-medium">{config.subtitle}</p>
                                    <p className="text-white/50 text-[10px] mt-0.5 line-clamp-2">{config.description}</p>
                                </div>
                            </div>
                            {/* Right - form mock */}
                            <div className="bg-background flex items-center justify-center p-4">
                                <div className="w-full max-w-[200px] space-y-3">
                                    <div>
                                        <p className="font-bold text-sm uppercase tracking-tight">{config.card_title}</p>
                                        <p className="text-[10px] text-muted-foreground">{config.card_description}</p>
                                    </div>
                                    <div className="space-y-1.5">
                                        <div className="h-6 bg-muted rounded border text-[10px] px-2 flex items-center text-muted-foreground">
                                            usuario@mlp.cl
                                        </div>
                                        <div className="h-6 bg-muted rounded border text-[10px] px-2 flex items-center text-muted-foreground">
                                            ******
                                        </div>
                                    </div>
                                    <div
                                        className="h-7 rounded flex items-center justify-center text-white text-[10px] font-bold uppercase"
                                        style={{ backgroundColor: config.accent_color }}
                                    >
                                        Iniciar Sesion
                                    </div>
                                    <p className="text-[8px] text-muted-foreground text-center">{config.footer_text}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── Save Button ── */}
            <div className="flex justify-end">
                <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="min-w-[200px]"
                    data-testid="save-login-config-btn"
                >
                    {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                        <Save className="h-4 w-4 mr-2" />
                    )}
                    Guardar Configuracion
                </Button>
            </div>
        </div>
    );
}
